import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ClinicalStepper } from "@/components/common/ClinicalStepper";
import { initials, formatDate, STAGE_LABELS, STAGES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowRight, FileText, Sparkles, Boxes, ClipboardCheck, Activity, ChevronRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{label}</div>
      <div className="mt-1 text-xl font-extrabold text-[#101516] dark:text-white tabular-nums">{value || "—"}</div>
    </div>
  );
}

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = () => api.get(`/cases/${id}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="space-y-4"><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>;
  const { case: c, patient: p, reports } = data;
  const site = c.implant_site || {};

  const advance = async () => {
    try { await api.post(`/cases/${id}/advance`); toast.success("Case advanced"); load(); }
    catch { toast.error("Could not advance case"); }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data: report } = await api.post("/ai/report/generate", { case_id: id, type: "Implant Assessment" });
      toast.success("AI report drafted — review required");
      navigate(`/reports/${report.id}`);
    } catch { toast.error("Report generation failed"); }
    finally { setGenerating(false); }
  };

  const isLast = c.stage === STAGES[STAGES.length - 1];

  return (
    <div>
      <PageHeader breadcrumb={["Workspace", "Cases", c.case_id]} title={c.title}
        subtitle={`${c.case_id} · ${c.treatment}`}
        actions={<>
          {!isLast && <Button onClick={advance} variant="outline" data-testid="advance-case-btn" className="rounded-lg border-[#E2E8F0] dark:border-[#223139]">Advance Stage <ArrowRight className="h-4 w-4 ml-1.5" /></Button>}
          <Button onClick={generateReport} disabled={generating} data-testid="generate-report-btn" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
            {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Generate AI Report
          </Button>
        </>}
      />

      {/* Journey */}
      <SurfaceCard className="p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => p && navigate(`/patients/${p.id}`)} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F7F0] dark:bg-[#00A85A]/15 text-xs font-bold text-[#008F4D] dark:text-[#3FD98A]">{initials(p?.name)}</button>
            <div><div className="text-sm font-bold text-[#101516] dark:text-white">{p?.name}</div><div className="text-xs text-[#8B9698]">{p?.patient_id} · Current stage: <span className="font-semibold text-[#00A85A]">{STAGE_LABELS[c.stage]}</span></div></div>
          </div>
          <div className="text-right hidden sm:block"><div className="text-xs text-[#8B9698]">Readiness</div><div className="text-lg font-extrabold text-[#00A85A]">{c.readiness}%</div></div>
        </div>
        <ClinicalStepper current={c.stage} done={c.stages_done || []} />
      </SurfaceCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Diagnosis & findings */}
          <SurfaceCard className="p-5">
            <div className="flex items-center gap-2 mb-3"><Activity className="h-4 w-4 text-[#00A85A]" /><h3 className="text-sm font-bold text-[#101516] dark:text-white">Diagnosis & Clinical Findings</h3></div>
            <p className="text-sm text-[#687173] dark:text-[#8B9698] mb-3">{c.diagnosis || "No diagnosis recorded yet."}</p>
            <div className="flex flex-wrap gap-2">{(c.findings || []).map((f, i) => <span key={i} className="rounded-lg bg-[#F4FBF7] dark:bg-[#0E1A21] border border-[#E2E8F0] dark:border-[#223139] px-3 py-1.5 text-xs font-medium text-[#101516] dark:text-white">{f}</span>)}</div>
          </SurfaceCard>

          {/* Implant site */}
          <SurfaceCard className="p-5">
            <div className="flex items-center gap-2 mb-4"><Boxes className="h-4 w-4 text-[#00A85A]" /><h3 className="text-sm font-bold text-[#101516] dark:text-white">Implant Site — Tooth {site.tooth || "—"}</h3></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Bone Height" value={site.bone_height} />
              <Metric label="Bone Width" value={site.bone_width} />
              <Metric label="IAN Clearance" value={site.ian_clearance} />
              <Metric label="Bone Type" value={site.bone_type} />
            </div>
          </SurfaceCard>

          {/* Treatment plan */}
          <SurfaceCard className="p-5">
            <div className="flex items-center gap-2 mb-4"><ClipboardCheck className="h-4 w-4 text-[#00A85A]" /><h3 className="text-sm font-bold text-[#101516] dark:text-white">Treatment Plan</h3></div>
            <div className="space-y-2.5">
              {(c.treatment_plan || []).map((ph, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] dark:border-[#223139] p-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${ph.status === "completed" ? "bg-[#00A85A] text-white" : ph.status === "in_progress" ? "bg-[#E8F7F0] text-[#008F4D] dark:bg-[#00A85A]/15 dark:text-[#3FD98A]" : "bg-[#F8F9F8] dark:bg-[#223139] text-[#8B9698]"}`}>{i + 1}</div>
                  <div className="flex-1"><div className="text-sm font-semibold text-[#101516] dark:text-white">{ph.phase}</div><div className="text-xs text-[#8B9698]">{ph.description}</div></div>
                  <StatusBadge status={ph.status} />
                </div>
              ))}
            </div>
          </SurfaceCard>
        </div>

        <div className="space-y-6">
          {/* Reports */}
          <SurfaceCard className="p-5">
            <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-bold text-[#101516] dark:text-white">Reports</h3><FileText className="h-4 w-4 text-[#8B9698]" /></div>
            {reports.length === 0 ? <p className="text-sm text-[#8B9698]">No reports yet. Generate an AI report to get started.</p> :
              <div className="space-y-2">{reports.map((r) => (
                <button key={r.id} onClick={() => navigate(`/reports/${r.id}`)} className="flex w-full items-center gap-2 rounded-lg border border-[#E2E8F0] dark:border-[#223139] p-3 text-left hover:border-[#00A85A]/40">
                  <FileText className="h-4 w-4 text-[#00A85A]" /><div className="flex-1 min-w-0"><div className="text-xs font-semibold text-[#101516] dark:text-white truncate">{r.type}</div><div className="text-[10px] text-[#8B9698]">{formatDate(r.created_at)}</div></div><StatusBadge status={r.status} />
                </button>))}</div>}
          </SurfaceCard>

          {/* AI card */}
          <SurfaceCard dark className="p-5">
            <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-[#3FD98A]" /><span className="text-sm font-bold text-white">CONTAHA AI</span></div>
            <p className="text-xs text-[#8B9698] mb-3">Draft a structured clinical report from this case's data. You review and approve before it's finalized.</p>
            <Button onClick={generateReport} disabled={generating} className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
              {generating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Drafting...</> : "Draft Report with AI"}
            </Button>
            <div className="mt-3 text-[10px] text-[#4E5F68] text-center">AI assists. Clinician decides.</div>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
